import { useTranslation } from '../../../application/hooks/useTranslation';
import {
  getInstallmentStatusSpec,
  type GridSlot,
} from '../../../domain/calculators/installments';
import { InstallmentStatus } from '../../../domain/enums/SystemEnums';
import { getSlotMonthLabel } from './installmentUtils';
import './installments.css';

export interface MonthCellProps {
  slot: GridSlot;
  isDimmed?: boolean;
  isHighlighted?: boolean;
  onSelect?: (slot: GridSlot) => void;
  onHover?: (slot: GridSlot) => void;
  onLeave?: () => void;
}

/**
 * Solidified Interactive Month Cell (DEVELOPMENT_PLAN.md §6.3 / §3.1 / §3.2).
 * Shows month number, calendar month from row.dueDate (or empty if absent),
 * and status glyph from the unified status table.
 */
export function MonthCell({
  slot,
  isDimmed,
  isHighlighted,
  onSelect,
  onHover,
  onLeave,
}: MonthCellProps) {
  const { t } = useTranslation();
  const monthName = getSlotMonthLabel(slot);
  const statusSpec = getInstallmentStatusSpec(slot.row?.status, slot.row?.dueDate);
  const statusLabel = t(statusSpec.labelKey);

  const isAdvanced = slot.row?.status === InstallmentStatus.ADVANCED;
  const advanceHint = isAdvanced && monthName
    ? ` • ${t('installments.advanceHint', { month: monthName })}`
    : '';

  const title = slot.row
    ? `${t('installments.month')} ${slot.installmentNumber}${
        monthName ? ` (${monthName})` : ''
      } — ${statusLabel}${advanceHint}${slot.duplicate ? ' *' : ''}`
    : `${t('installments.month')} ${slot.installmentNumber} — ${t('installments.not_generated')}`;

  return (
    <button
      type="button"
      className={`mg-cell mg-cell--${slot.phase}${
        isDimmed ? ' mg-cell--dimmed' : ''
      }${isHighlighted ? ' mg-cell--highlighted' : ''}`}
      data-testid="month-cell"
      data-month={slot.installmentNumber}
      data-phase={slot.phase}
      data-duplicate={slot.duplicate ? 'true' : undefined}
      title={title}
      aria-label={title}
      onClick={() => onSelect?.(slot)}
      onMouseEnter={() => onHover?.(slot)}
      onMouseLeave={() => onLeave?.()}
      onFocus={() => onHover?.(slot)}
      onBlur={() => onLeave?.()}
    >
      <div className="mg-cell-top">
        <span className="mg-cell-num">{slot.installmentNumber}</span>
        {monthName ? <span className="mg-cell-month-label">{monthName}</span> : null}
      </div>

      <div className="mg-cell-bottom">
        <span className="mg-cell-mark" aria-hidden="true">
          {statusSpec.glyph}
        </span>
      </div>

      {slot.phase === 'due' && (
        <span className="mg-cell-radar" aria-hidden="true" />
      )}
    </button>
  );
}

export default MonthCell;
